/**
 * The guided intake frame (INTAKE_GUIDED_FLOW_2026-10-03 §3.1): one step per screen over a
 * full-window stage. A header strip (name, the four-step rail, Full editor, ×) and ONE step card,
 * both glass, both drawn from `intakeSteps` — the frame decides nothing, it performs the action a
 * screen names on the person's click.
 *
 * It is chrome tier, not a panel: the workspace stays mounted underneath, hidden by
 * `body[data-intake="guided"]`, so leaving to the Full editor loses no state. Whether the frame is
 * guiding is ONE predicate (`guided()`), which main.ts uses to keep every detector overlay off the
 * stage whatever the register (the hair-highlight leak, design §1 finding 2).
 *
 * TEXT ONLY: World's name and reasons are display text from another page — textContent, never
 * innerHTML.
 *
 * Depends on: intake-session (state, export, close), intake-view (`intakeSteps`), status-mark.
 * Depended on by: main.ts.
 */
import type { IntakeSession } from '../intake/intake-session.js';
import { avatarName, intakeSteps, type StepAction, type StepScreen } from '../intake/intake-view.js';
import { statusMark } from './status-mark.js';

export interface IntakeFrameDeps {
  readonly session: IntakeSession;
  /** The stage's label, for an opt-in session's name (display only). */
  readonly stageName: () => string;
  /**
   * The frame turned on or off, or its stage changed (or it moved into or out of adding fingers): main
   * applies the attribute, the overlays, the first frame, the proof stage. `screen` is null when off.
   */
  readonly changed: (guided: boolean, screen: StepScreen | null) => void;
  /** Back to World (design §3.6; PR 3 owns the whole return). */
  readonly returnToWorld: () => void;
  /** Is the World tab that opened Harry still there? Read at render: the Sent copy says where to go. */
  readonly worldTab?: () => 'open' | 'gone';
}

export interface IntakeFrame {
  /** The one prompt for World's hand-off: resolves on Open it (true) or Not now (false). */
  readonly ask: (offer: { readonly name: string; readonly reasons: readonly string[]; readonly ageMs: number }) => Promise<boolean>;
  /** `?handoff=1` found nothing usable: the nothing-waiting screen. */
  readonly missing: () => void;
  /** Is the nothing-waiting screen up (a drop then starts an opt-in session)? */
  readonly waitingForDrop: () => boolean;
  /** Is the frame guiding right now? The overlay predicate. */
  readonly guided: () => boolean;
  /** Back from the Full editor to the step the person left (the relabelled ✓ World check). */
  readonly resume: () => void;
  /** The screen on show, or null — the e2e's evidence surface. */
  readonly probe: () => StepScreen | null;
  /** The browser refused to close the tab on Back to World: the card says where World is (design §3.6). */
  readonly stayed: () => void;
  /**
   * No World tab behind this one, but a World tab acknowledged THIS sha (world-2d): the card says so — only
   * while it is still the session's last-sent record (Codex P2, #244: B never shows A's acknowledgement).
   */
  readonly worldHasIt: (sha256: string) => void;
  /** Keep improving in Harry: the full editor with the one-line hint bar (Back to World, Send again). */
  readonly improving: () => boolean;
}

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

export function createIntakeFrame(host: HTMLElement, deps: IntakeFrameDeps): IntakeFrame {
  let arrival: { kind: 'offer'; name: string; ageMs: number } | { kind: 'missing' } | null = null;
  let answer: ((yes: boolean) => void) | null = null;
  /** Open was clicked; the session has not spoken yet. */
  let opening = false;
  let confirmLeave = false;
  /** Back to World could not close the tab (Codex P2, #241): said in the card, never only on the hidden line. */
  let stayed = false;
  /** The sha a World tab acknowledged; it counts only while it is the session's last-sent record. */
  let ackedSha: string | null = null;
  /** The person chose the Full editor: the session stays, the frame steps aside. */
  let editor = false;
  let shown: StepScreen | null = null;
  /** Disclosures the person opened, kept across re-renders of the same screen. */
  const disclosed = new Set<string>();

  const head = el('div', 'intake-frame-head');
  head.dataset['testid'] = 'intake-frame-head';
  const card = el('section', 'intake-step-card');
  card.dataset['testid'] = 'intake-step';
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-live', 'polite');
  head.hidden = true;
  card.hidden = true;
  /**
   * Keep improving in Harry (Allen, 2026-10-03: "it's important to allow the editor"): the guided frame
   * steps aside into the FULL editor, and one line stays — what was saved, where to look, and the two ways
   * out (send the improved file again, or go back to World with what was sent).
   */
  let improving = false;
  const bar = el('div', 'intake-improve-bar');
  bar.dataset['testid'] = 'intake-improve';
  bar.setAttribute('role', 'status');
  bar.hidden = true;
  host.append(head, card, bar);

  const live = (): boolean => !editor && (arrival !== null || opening || deps.session.active());

  const nameNow = (): string => {
    const s = deps.session.state();
    if (s !== null && s.origin.kind === 'world') return s.origin.name;
    if (arrival?.kind === 'offer') return arrival.name;
    return deps.stageName();
  };

  const act = (action: StepAction): void => {
    switch (action) {
      case 'open':
        opening = true;
        arrival = null;
        answer?.(true);
        answer = null;
        break;
      case 'not-now':
        arrival = null;
        editor = true;
        answer?.(false);
        answer = null;
        break;
      case 'next':
      case 'keep-hands':
      case 'add-fingers':
      case 'fingers-done':
      case 'let-sway':
      case 'keep-still':
      case 'grow-tail':
      case 'tail-done':
      case 'tail-leave':
      case 'fix-again':
      case 'see-walk':
      case 'retry-walk':
      case 'back':
        void deps.session.act(action);
        break;
      case 'send':
      case 'save-copy':
        // F (PR 3): the record from the bytes Check showed — no rebuild, no save dialog (ADR-040 §2 as amended).
        void deps.session.act(action);
        break;
      case 'full-editor':
        editor = true;
        arrival = arrival?.kind === 'missing' ? null : arrival;
        break;
      case 'keep-improving':
        editor = true;
        improving = true;
        break;
      case 'back-to-world':
        deps.returnToWorld();
        break;
      case 'keep-fixing':
        confirmLeave = false;
        break;
      case 'stop':
        confirmLeave = false;
        arrival = null;
        opening = false;
        deps.session.close();
        break;
    }
    render();
  };

  const button = (label: string, action: StepAction, primary: boolean): HTMLButtonElement => {
    const b = el('button', primary ? 'hand-graft-now intake-step-primary' : 'hand-revert intake-step-secondary', label);
    b.type = 'button';
    b.dataset['action'] = action;
    b.addEventListener('click', () => act(action));
    return b;
  };

  const drawHead = (s: StepScreen): void => {
    const sess = deps.session.state();
    const who = avatarName(nameNow());
    const line =
      s.id === 'arriving' || s.id === 'nothing-waiting'
        ? 'from World'
        : sess?.origin.kind === 'opt-in'
          ? `checking ${who} for World`
          : `fixing ${who} for World`;
    // The public name is "Harry" (Allen, 2026-10-03): the wordmark leads the strip, and the tab says what is open.
    const name = el('span', 'intake-frame-name');
    const mark = el('span', 'intake-wordmark', 'Harry');
    mark.dataset['testid'] = 'intake-wordmark';
    name.append(mark, el('span', 'intake-frame-what', line));
    const children: HTMLElement[] = [name];
    document.title = s.id === 'arriving' || s.id === 'nothing-waiting' ? 'Harry — from World' : `Harry — fixing ${who}`;
    const railBox = el('ol', 'intake-rail');
    railBox.setAttribute('aria-label', 'Steps');
    if (s.rail !== null) {
      for (const r of s.rail) {
        const li = el('li', 'intake-rail-step');
        li.dataset['mark'] = r.mark ?? 'ahead';
        if (r.mark === 'needs') li.setAttribute('aria-current', 'step');
        if (r.mark !== null) li.append(statusMark(r.mark, { subject: r.label, pill: null }));
        li.append(el('span', 'intake-rail-label', r.label));
        railBox.append(li);
      }
    }
    railBox.hidden = s.rail === null;
    children.push(railBox);
    const doors = el('span', 'intake-frame-doors');
    // Back to World is always one click away while the guided frame is up — the arrival screens too
    // (Allen, 2026-10-03: "hard to find"; Codex P2, #244).
    const back = el('button', 'intake-frame-world', 'Back to World');
    back.type = 'button';
    back.dataset['action'] = 'back-to-world';
    back.dataset['testid'] = 'intake-frame-world';
    back.addEventListener('click', () => act('back-to-world'));
    doors.append(back);
    if (s.id !== 'arriving' && s.id !== 'nothing-waiting') {
      const full = el('button', 'hand-revert intake-frame-editor', 'Full editor');
      full.type = 'button';
      full.dataset['action'] = 'full-editor';
      full.addEventListener('click', () => act('full-editor'));
      const close = el('button', 'intake-frame-close', '×');
      close.type = 'button';
      close.dataset['action'] = 'close';
      close.setAttribute('aria-label', 'Stop fixing');
      close.addEventListener('click', () => {
        confirmLeave = true;
        render();
      });
      doors.append(full, close);
    }
    children.push(doors);
    head.replaceChildren(...children);
  };

  const drawCard = (s: StepScreen): void => {
    card.dataset['screen'] = s.id;
    const children: HTMLElement[] = [el('h2', 'intake-step-title', s.title)];
    for (const p of s.body) children.push(el('p', 'intake-step-body', p));
    if (s.notice !== null) children.push(el('p', 'intake-step-notice', s.notice));
    if (s.lines.length > 0) {
      const list = el('ul', 'intake-step-lines');
      for (const line of s.lines) {
        const li = el('li', '');
        li.append(statusMark('fine', { pill: null }), el('span', '', line));
        list.append(li);
      }
      children.push(list);
    }
    if (s.busy) {
      const bar = el('div', 'intake-step-busy');
      bar.setAttribute('role', 'progressbar');
      bar.setAttribute('aria-label', 'Working');
      children.push(bar);
    }
    const row = el('div', 'intake-step-actions');
    const left = el('div', 'intake-step-left');
    if (s.disclosure !== null) {
      // The disclosure's BODY opens above the button row; its toggle stays bottom-left and the
      // primary bottom-right, so opening it never moves a button (the one-spot rule, design §3.1).
      const key = `${s.id}|${s.disclosure.label}`;
      const body = el('div', 'intake-step-more');
      body.id = `intake-more-${s.id}`;
      const list = el('ul', '');
      for (const line of s.disclosure.lines) list.append(el('li', '', line));
      body.append(list);
      body.hidden = !disclosed.has(key);
      children.push(body);
      const toggle = el('button', 'intake-step-more-toggle', s.disclosure.label);
      toggle.type = 'button';
      toggle.setAttribute('aria-controls', body.id);
      toggle.setAttribute('aria-expanded', String(!body.hidden));
      toggle.addEventListener('click', () => {
        body.hidden = !body.hidden;
        if (body.hidden) disclosed.delete(key);
        else disclosed.add(key);
        toggle.setAttribute('aria-expanded', String(!body.hidden));
      });
      left.append(toggle);
    } else if (s.secondary !== null) {
      left.append(button(s.secondary.label, s.secondary.action, false));
    }
    if (s.also !== null) left.append(button(s.also.label, s.also.action, false));
    row.append(left);
    if (s.disclosure !== null && s.secondary !== null) row.append(button(s.secondary.label, s.secondary.action, false));
    if (s.primary !== null) row.append(button(s.primary.label, s.primary.action, true));
    if (s.primary !== null || s.secondary !== null || s.disclosure !== null) children.push(row);
    card.replaceChildren(...children);
    card.querySelector<HTMLButtonElement>('.intake-step-primary')?.focus({ preventScroll: true });
  };

  /** The page's own title, given back when the frame steps aside. */
  const titleBefore = document.title;
  // Born off: the first render says nothing (main's overlay hooks are declared after the frame).
  let lastKey = 'off';
  const drawImproveBar = (): void => {
    const show = improving && editor && deps.session.active();
    bar.hidden = !show;
    if (!show) return;
    const hasIt = ackedSha !== null && deps.session.lastSent()?.sha256 === ackedSha;
    const text = el(
      'span',
      'intake-improve-text',
      hasIt ? 'Your World tab has it. Switch to it.' : "Your fixes are saved. Look ▸ to adjust shine and materials; Back to World when you're done.",
    );
    const again = el('button', 'hand-revert intake-improve-again', 'Send again ›');
    again.type = 'button';
    again.dataset['action'] = 'send-again';
    again.title = 'Fix and check the improved file, then send it to World';
    again.addEventListener('click', () => {
      improving = false;
      editor = false;
      void deps.session.act('fix-again');
      render();
    });
    const back = el('button', 'intake-frame-world', 'Back to World');
    back.type = 'button';
    back.dataset['action'] = 'back-to-world';
    back.addEventListener('click', () => deps.returnToWorld());
    bar.replaceChildren(text, again, back);
  };
  const render = (): void => {
    const sess = deps.session.state();
    if (sess !== null) opening = false;
    const on = live();
    drawImproveBar();
    if (!on) {
      head.hidden = true;
      card.hidden = true;
      delete document.body.dataset['intakeScreen'];
      document.title = titleBefore;
      if (shown !== null || lastKey !== 'off') {
        shown = null;
        lastKey = 'off';
        deps.changed(false, null);
      }
      return;
    }
    const placeholder = opening && sess === null;
    const s = intakeSteps({
      arrival,
      session: placeholder ? { origin: { kind: 'opt-in' }, checking: true, error: null, report: null, onStage: false, exported: null } : sess,
      confirmLeave,
      name: nameNow(),
      stayed,
      worldHasIt: ackedSha !== null && deps.session.lastSent()?.sha256 === ackedSha,
      worldTab: deps.worldTab?.() ?? 'open',
    });
    shown = s;
    head.hidden = false;
    card.hidden = false;
    drawHead(s);
    drawCard(s);
    card.dataset['slim'] = String(s.slim);
    document.body.dataset['intakeScreen'] = s.id;
    const key = `on|${s.stage}|${s.id === 'doing-fingers'}`;
    if (key !== lastKey) {
      lastKey = key;
      deps.changed(true, s);
    }
  };

  deps.session.subscribe(() => {
    if (!deps.session.active()) {
      stayed = false;
      ackedSha = null;
      improving = false;
    }
    render();
  });
  window.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !live() || shown === null) return;
    if (shown.id === 'arriving' || shown.id === 'nothing-waiting') return;
    confirmLeave = !confirmLeave;
    render();
  });
  render();

  return {
    ask: (offer) =>
      new Promise<boolean>((resolve) => {
        arrival = { kind: 'offer', name: offer.name, ageMs: offer.ageMs };
        editor = false;
        answer = resolve;
        render();
      }),
    missing: () => {
      arrival = { kind: 'missing' };
      editor = false;
      render();
    },
    waitingForDrop: () => arrival?.kind === 'missing' && !editor,
    guided: () => live(),
    resume: () => {
      editor = false;
      improving = false;
      if (arrival?.kind === 'missing') arrival = null;
      render();
    },
    probe: () => shown,
    stayed: () => {
      stayed = true;
      render();
    },
    improving: () => improving && editor && deps.session.active(),
    worldHasIt: (sha256) => {
      ackedSha = sha256;
      render();
    },
  };
}
